import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useDeliverables } from "@/hooks/useLive";
import type { Category, Deliverable, Proposal, ProposalKind, Status } from "@/lib/types";
import { CATEGORIES, STATUS_MAP } from "@/data/categories";
import { ROSTER } from "@/data/roster";
import { cn } from "@/lib/utils";
import { Sparkles, Send, Check, X, Loader2, FileText, Pencil, Lightbulb, RotateCcw, RefreshCw, ExternalLink } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { resolvePerson } from "@/lib/roster";

const KIND_LABEL: Record<ProposalKind, string> = {
  status_change: "Status",
  owner_change: "Owner",
  due_change: "Due date",
  phase_change: "Phase",
  category_change: "Category",
  name_change: "Title",
  dependency_add: "Dependency",
  flag_change: "Flag",
  new_task: "New task",
  comment: "Comment",
};

function parsePatch(p: Proposal): Record<string, unknown> {
  try { return JSON.parse(p.patchJson || "{}"); } catch { return {}; }
}

function before(field: string, task: Deliverable | undefined): string {
  if (!task) return "—";
  const v = (task as unknown as Record<string, unknown>)[field];
  return (v === undefined || v === null || v === "") ? "—" : String(v);
}

function ProposalCard({ p, tasks, onApprove, onReject, onRestore }: {
  p: Proposal;
  tasks: Deliverable[];
  onApprove: (overridePatch?: Record<string, unknown>) => Promise<void>;
  onReject: () => Promise<void>;
  onRestore?: () => Promise<void>;
}) {
  const patch = parsePatch(p);
  const task = tasks.find((t) => String(t.id) === p.taskId);
  const [busy, setBusy] = useState<"approve" | "reject" | "restore" | null>(null);
  const [editing, setEditing] = useState(false);

  // Reserved meta keys stripped of __ prefix for display
  const relatedIds = Array.isArray(patch.__related_task_ids) ? (patch.__related_task_ids as string[]) : [];
  const placement = typeof patch.__placement_notes === "string" ? patch.__placement_notes : "";
  const relatedTasks = relatedIds.map((id) => tasks.find((t) => String(t.id) === id)).filter(Boolean) as Deliverable[];
  const alignmentScore = typeof patch.__alignment_score === "number" ? (patch.__alignment_score as number) : null;
  const alignmentNotes = typeof patch.__alignment_notes === "string" ? (patch.__alignment_notes as string) : "";
  const sourceDocUrl = typeof patch.__source_doc_url === "string" ? (patch.__source_doc_url as string) : "";

  const decide = async (action: "approve" | "reject" | "restore", override?: Record<string, unknown>) => {
    setBusy(action);
    try {
      if (action === "approve") await onApprove(override);
      else if (action === "reject") await onReject();
      else if (action === "restore" && onRestore) await onRestore();
    } finally { setBusy(null); }
  };

  const head = p.kind === "new_task"
    ? `NEW · ${String(patch.name ?? "Untitled")}`
    : task ? `#${task.id} · ${task.name}` : `#${p.taskId}`;

  return (
    <li className="rounded-lg border border-border bg-card p-4 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="rounded-full bg-muted px-2 py-0.5 text-micro font-medium uppercase tracking-wider text-muted-foreground">
              {KIND_LABEL[p.kind]}
            </span>
            <span className="truncate text-caption font-medium text-foreground">{head}</span>
            {alignmentScore !== null && (
              <span
                title={alignmentNotes || `Agent's alignment to Felix GA: ${alignmentScore}%`}
                className={cn(
                  "ml-auto inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-micro font-semibold",
                  alignmentScore >= 80 ? "bg-emerald-100 text-emerald-700" :
                  alignmentScore >= 60 ? "bg-amber-100 text-amber-700" :
                  "bg-rose-100 text-rose-700"
                )}
              >
                {alignmentScore}% aligned
              </span>
            )}
          </div>

          <div className="mt-2 space-y-1 text-caption">
            <DiffRow proposal={p} patch={patch} task={task} />
          </div>

          {p.reason && (
            <div className="mt-3 border-l-2 border-muted-foreground/20 pl-3 text-caption italic text-muted-foreground">
              {p.reason}
              {sourceDocUrl && (
                <a
                  href={sourceDocUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="ml-2 inline-flex items-center gap-1 text-caption not-italic text-primary hover:underline"
                  title="Open the source transcript in Google Docs (Cmd+F to find this quote)"
                >
                  <ExternalLink size={11} /> Open transcript
                </a>
              )}
            </div>
          )}

          {alignmentNotes && (
            <div className="mt-2 text-micro text-muted-foreground">
              <span className="font-medium text-foreground">Why this score:</span> {alignmentNotes}
            </div>
          )}

          {placement && (
            <div className="mt-3 flex gap-2 rounded-md bg-ai-purple/5 p-3 text-caption text-foreground">
              <Lightbulb size={12} className="mt-0.5 shrink-0 text-ai-purple" />
              <div>
                <div className="mb-0.5 text-micro font-semibold uppercase tracking-wider text-ai-purple">Agent's reasoning</div>
                <div className="text-caption text-muted-foreground">{placement}</div>
              </div>
            </div>
          )}

          {relatedTasks.length > 0 && (
            <div className="mt-3">
              <div className="mb-1 text-micro font-semibold uppercase tracking-wider text-muted-foreground">Related tasks</div>
              <div className="flex flex-wrap gap-1.5">
                {relatedTasks.map((t) => (
                  <span key={t.id} className="inline-flex items-center gap-1 rounded-full border border-border bg-muted/40 px-2 py-0.5 text-micro text-foreground">
                    <span className={cn("h-1.5 w-1.5 rounded-full", STATUS_MAP[t.status]?.dot)} />
                    #{t.id} {t.name}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="flex shrink-0 flex-col gap-1.5">
          {p.status === "pending" && (
            <>
              <button
                disabled={busy !== null}
                onClick={() => decide("approve")}
                className="inline-flex items-center gap-1 rounded-md bg-primary px-3 py-1.5 text-caption font-medium text-white hover:bg-primary/90 disabled:opacity-50"
              >
                {busy === "approve" ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
                Approve
              </button>
              <button
                disabled={busy !== null}
                onClick={() => setEditing((v) => !v)}
                className="inline-flex items-center gap-1 rounded-md border border-border bg-card px-3 py-1.5 text-caption text-foreground hover:bg-muted disabled:opacity-50"
              >
                <Pencil size={12} /> {editing ? "Cancel edit" : "Edit"}
              </button>
              <button
                disabled={busy !== null}
                onClick={() => decide("reject")}
                className="inline-flex items-center gap-1 rounded-md border border-border bg-card px-3 py-1.5 text-caption text-foreground hover:bg-muted disabled:opacity-50"
              >
                {busy === "reject" ? <Loader2 size={12} className="animate-spin" /> : <X size={12} />}
                Reject
              </button>
            </>
          )}
          {p.status === "rejected" && onRestore && (
            <button
              disabled={busy !== null}
              onClick={() => decide("restore")}
              className="inline-flex items-center gap-1 rounded-md border border-border bg-card px-3 py-1.5 text-caption text-foreground hover:border-primary/40 hover:bg-primary/5 hover:text-primary disabled:opacity-50"
            >
              {busy === "restore" ? <Loader2 size={12} className="animate-spin" /> : <RotateCcw size={12} />}
              Restore to pending
            </button>
          )}
          {p.status === "approved" && (
            <span className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-caption text-emerald-700">Approved</span>
          )}
        </div>
      </div>

      {(p.status === "rejected" || p.status === "approved") && (p.decidedBy || p.decidedAt) && (
        <div className="mt-3 border-t border-border pt-2 text-micro text-muted-foreground">
          {p.status === "rejected" ? "Rejected" : "Approved"} by {p.decidedBy || "?"} · {p.decidedAt ? new Date(p.decidedAt).toLocaleString() : ""}
        </div>
      )}

      {editing && (
        <ProposalEditor
          proposal={p}
          patch={patch}
          tasks={tasks}
          busy={busy === "approve"}
          onSaveAndApprove={async (overridePatch) => {
            await decide("approve", overridePatch);
            setEditing(false);
          }}
        />
      )}
    </li>
  );
}

function ProposalEditor({ proposal, patch, tasks, busy, onSaveAndApprove }: {
  proposal: Proposal;
  patch: Record<string, unknown>;
  tasks: Deliverable[];
  busy: boolean;
  onSaveAndApprove: (override: Record<string, unknown>) => Promise<void>;
}) {
  const k = proposal.kind;
  // Initialize state from current patch (minus reserved meta keys)
  const [name, setName] = useState(String(patch.name ?? ""));
  const [owner, setOwner] = useState(String(patch.owner ?? ""));
  const [due, setDue] = useState(String(patch.due ?? ""));
  const [status, setStatus] = useState<Status>(((patch.status as Status) ?? "not_started") as Status);
  const [phase, setPhase] = useState(String(patch.phase ?? ""));
  const [category, setCategory] = useState<Category>(((patch.category as Category) ?? "systems") as Category);
  const [flag, setFlag] = useState(String(patch.flag ?? ""));
  const [body, setBody] = useState(String(patch.body ?? ""));
  const [dependencies, setDependencies] = useState(String(patch.dependencies ?? ""));
  const [notes, setNotes] = useState(String(patch.notes ?? ""));
  const [depId, setDepId] = useState(String(patch.dependency_id ?? ""));

  const submit = async () => {
    let override: Record<string, unknown> = {};
    switch (k) {
      case "status_change":   override = { status }; break;
      case "owner_change":    override = { owner }; break;
      case "due_change":      override = { due }; break;
      case "phase_change":    override = { phase }; break;
      case "category_change": override = { category }; break;
      case "name_change":     override = { name }; break;
      case "flag_change":     override = { flag }; break;
      case "dependency_add":  override = { dependency_id: depId }; break;
      case "comment":         override = { body }; break;
      case "new_task":
        override = {
          name, owner, due, status, phase, category,
          ...(flag ? { flag } : {}),
          ...(dependencies ? { dependencies } : {}),
          ...(notes ? { notes } : {}),
        };
        break;
    }
    await onSaveAndApprove(override);
  };

  return (
    <div className="mt-4 space-y-3 rounded-md border border-border bg-muted/30 p-3">
      <div className="text-micro font-semibold uppercase tracking-wider text-muted-foreground">Edit before approving</div>

      {k === "comment" && (
        <Field label="Comment body">
          <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={3} className={inputCls} />
        </Field>
      )}
      {k === "status_change" && (
        <Field label="New status">
          <select value={status} onChange={(e) => setStatus(e.target.value as Status)} className={inputCls}>
            {(Object.keys(STATUS_MAP) as Status[]).map((s) => <option key={s} value={s}>{STATUS_MAP[s].label}</option>)}
          </select>
        </Field>
      )}
      {k === "owner_change" && <Field label="New owner"><OwnerInput value={owner} onChange={setOwner} /></Field>}
      {k === "name_change" && <Field label="New title"><input className={inputCls} value={name} onChange={(e) => setName(e.target.value)} /></Field>}
      {k === "due_change" && <Field label="New due"><input className={inputCls} value={due} onChange={(e) => setDue(e.target.value)} placeholder="e.g. May 11" /></Field>}
      {k === "phase_change" && (
        <Field label="New phase">
          <select value={phase} onChange={(e) => setPhase(e.target.value)} className={inputCls}>
            {["", "Alpha+", "Closed Beta 1", "Closed Beta 2", "GA"].map((v) => <option key={v} value={v}>{v || "—"}</option>)}
          </select>
        </Field>
      )}
      {k === "category_change" && (
        <Field label="New category">
          <select value={category} onChange={(e) => setCategory(e.target.value as Category)} className={inputCls}>
            {(Object.keys(CATEGORIES) as Category[]).map((c) => <option key={c} value={c}>{CATEGORIES[c].label}</option>)}
          </select>
        </Field>
      )}
      {k === "flag_change" && <Field label="Flag (empty to clear)"><input className={inputCls} value={flag} onChange={(e) => setFlag(e.target.value)} /></Field>}
      {k === "dependency_add" && (
        <Field label="Depends on (existing task id)">
          <DepPicker value={depId} onChange={setDepId} tasks={tasks} excludeId={proposal.taskId} />
        </Field>
      )}
      {k === "new_task" && (
        <div className="grid grid-cols-2 gap-3">
          <Field label="Title" wide><input className={inputCls} value={name} onChange={(e) => setName(e.target.value)} /></Field>
          <Field label="Owner"><OwnerInput value={owner} onChange={setOwner} /></Field>
          <Field label="Due"><input className={inputCls} value={due} onChange={(e) => setDue(e.target.value)} placeholder="e.g. May 11" /></Field>
          <Field label="Phase">
            <select value={phase} onChange={(e) => setPhase(e.target.value)} className={inputCls}>
              {["", "Alpha+", "Closed Beta 1", "Closed Beta 2", "GA"].map((v) => <option key={v} value={v}>{v || "—"}</option>)}
            </select>
          </Field>
          <Field label="Category">
            <select value={category} onChange={(e) => setCategory(e.target.value as Category)} className={inputCls}>
              {(Object.keys(CATEGORIES) as Category[]).map((c) => <option key={c} value={c}>{CATEGORIES[c].label}</option>)}
            </select>
          </Field>
          <Field label="Status">
            <select value={status} onChange={(e) => setStatus(e.target.value as Status)} className={inputCls}>
              {(Object.keys(STATUS_MAP) as Status[]).map((s) => <option key={s} value={s}>{STATUS_MAP[s].label}</option>)}
            </select>
          </Field>
          <Field label="Dependencies (comma-sep ids)" wide><input className={inputCls} value={dependencies} onChange={(e) => setDependencies(e.target.value)} placeholder="e.g. 42, 58" /></Field>
          <Field label="Flag (optional)" wide><input className={inputCls} value={flag} onChange={(e) => setFlag(e.target.value)} /></Field>
          <Field label="Notes" wide><textarea className={inputCls} rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} /></Field>
        </div>
      )}

      <div className="flex justify-end">
        <button
          onClick={submit}
          disabled={busy}
          className="inline-flex items-center gap-1 rounded-md bg-primary px-3 py-1.5 text-caption font-medium text-white hover:bg-primary/90 disabled:opacity-50"
        >
          {busy ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />} Save & approve
        </button>
      </div>
    </div>
  );
}

const inputCls = "w-full rounded-md border border-border bg-background px-2 py-1 text-caption text-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/20";

function Field({ label, children, wide }: { label: string; children: React.ReactNode; wide?: boolean }) {
  return (
    <div className={wide ? "col-span-2" : ""}>
      <div className="mb-1 text-micro uppercase tracking-wider text-muted-foreground">{label}</div>
      {children}
    </div>
  );
}

function OwnerInput({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <input
      list="roster-firstnames"
      className={inputCls}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder="First name"
    >
    </input>
  );
}

function DepPicker({ value, onChange, tasks, excludeId }: { value: string; onChange: (v: string) => void; tasks: Deliverable[]; excludeId: string }) {
  return (
    <select className={inputCls} value={value} onChange={(e) => onChange(e.target.value)}>
      <option value="">Select a task…</option>
      {tasks.filter((t) => String(t.id) !== excludeId).slice(0, 200).map((t) => (
        <option key={t.id} value={String(t.id)}>#{t.id} — {t.name}</option>
      ))}
    </select>
  );
}

function DiffRow({ proposal, patch, task }: { proposal: Proposal; patch: Record<string, unknown>; task: Deliverable | undefined }) {
  const p = proposal;
  switch (p.kind) {
    case "status_change":
      return <Diff label="Status" before={STATUS_MAP[(task?.status ?? "not_started")]?.label ?? task?.status ?? "—"} after={STATUS_MAP[String(patch.status) as keyof typeof STATUS_MAP]?.label ?? String(patch.status)} />;
    case "owner_change":
      return <Diff label="Owner" before={before("owner", task)} after={String(patch.owner ?? "")} />;
    case "due_change":
      return <Diff label="Due" before={before("due", task)} after={String(patch.due ?? "")} />;
    case "phase_change":
      return <Diff label="Phase" before={before("phase", task)} after={String(patch.phase ?? "")} />;
    case "category_change":
      return <Diff label="Category" before={CATEGORIES[task?.category as keyof typeof CATEGORIES]?.label ?? before("category", task)} after={CATEGORIES[String(patch.category) as keyof typeof CATEGORIES]?.label ?? String(patch.category)} />;
    case "name_change":
      return <Diff label="Title" before={before("name", task)} after={String(patch.name ?? "")} />;
    case "flag_change":
      return <Diff label="Flag" before={before("flag", task)} after={String(patch.flag ?? "") || "(cleared)"} />;
    case "dependency_add":
      return <Diff label="+ Depends on" before="" after={`#${String(patch.dependency_id ?? "")}`} />;
    case "comment":
      return <div className="whitespace-pre-wrap text-foreground"><span className="text-muted-foreground">Comment:</span> {String(patch.body ?? "")}</div>;
    case "new_task":
      return (
        <div className="space-y-0.5 text-foreground">
          <div><span className="text-muted-foreground">Name:</span> {String(patch.name ?? "")}</div>
          {patch.owner ? <div><span className="text-muted-foreground">Owner:</span> {String(patch.owner)}</div> : null}
          {patch.due ? <div><span className="text-muted-foreground">Due:</span> {String(patch.due)}</div> : null}
          {patch.category ? <div><span className="text-muted-foreground">Category:</span> {CATEGORIES[String(patch.category) as keyof typeof CATEGORIES]?.label ?? String(patch.category)}</div> : null}
        </div>
      );
    default:
      return <div className="text-muted-foreground">Unknown kind</div>;
  }
}

function Diff({ label, before, after }: { label: string; before: string; after: string }) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-muted-foreground">{label}:</span>
      <span className="line-through text-muted-foreground">{before || "—"}</span>
      <span className="text-muted-foreground">→</span>
      <span className="font-medium text-foreground">{after || "—"}</span>
    </div>
  );
}

interface SyncStatus {
  enabled: boolean;
  folderId: string | null;
  lastSyncAt: string | null;
  lastSyncSummary: {
    processed: { id: string; name: string; proposalsCount: number }[];
    skipped: { id: string; name: string; reason: string }[];
    errors: { id: string; name: string; error: string }[];
    durationMs?: number;
  } | null;
  inProgress: boolean;
  processedDocsCount: number;
}

export function ProposalsPanel() {
  const qc = useQueryClient();
  const tasks = useDeliverables();
  const [showManual, setShowManual] = useState(false);

  const { data: proposals = [], isLoading } = useQuery<Proposal[]>({
    queryKey: ["proposals"],
    queryFn: async () => {
      const res = await fetch("/api/proposals", { credentials: "include" });
      if (!res.ok) throw new Error(await res.text());
      return res.json();
    },
    staleTime: 10_000,
  });

  const { data: syncStatus } = useQuery<SyncStatus>({
    queryKey: ["proposals-sync-status"],
    queryFn: async () => {
      const res = await fetch("/api/proposals/sync/status", { credentials: "include" });
      if (!res.ok) throw new Error(await res.text());
      return res.json();
    },
    refetchInterval: 30_000,
    staleTime: 10_000,
  });

  const { data: processedDocs = [] } = useQuery<{ docId: string; docName: string; processedAt: string; proposalsCount: number }[]>({
    queryKey: ["proposals-processed-docs"],
    queryFn: async () => {
      const res = await fetch("/api/proposals/sync/processed", { credentials: "include" });
      if (!res.ok) return [];
      return res.json();
    },
    refetchInterval: 30_000,
    staleTime: 10_000,
  });

  const [rerunning, setRerunning] = useState<string | null>(null);
  const rerunDoc = async (docId: string, docName: string) => {
    if (!confirm(`Re-run agent on "${docName}"? Any pending proposals from this transcript will be rejected and replaced.`)) return;
    setRerunning(docId);
    setError(null);
    try {
      const res = await fetch(`/api/proposals/sync/doc/${encodeURIComponent(docId)}`, {
        method: "POST",
        credentials: "include",
      });
      if (!res.ok) throw new Error(await res.text());
      qc.invalidateQueries({ queryKey: ["proposals"] });
      qc.invalidateQueries({ queryKey: ["proposals-processed-docs"] });
      qc.invalidateQueries({ queryKey: ["proposals-sync-status"] });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Re-run failed");
    } finally {
      setRerunning(null);
    }
  };

  const [syncing, setSyncing] = useState(false);
  const triggerSync = async () => {
    setSyncing(true);
    setError(null);
    try {
      const res = await fetch("/api/proposals/sync", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: "{}",
        credentials: "include",
      });
      if (!res.ok) throw new Error(await res.text());
      qc.invalidateQueries({ queryKey: ["proposals"] });
      qc.invalidateQueries({ queryKey: ["proposals-sync-status"] });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Sync failed");
    } finally {
      setSyncing(false);
    }
  };

  const [source, setSource] = useState("");
  const [transcript, setTranscript] = useState("");
  const [proposing, setProposing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [recentCount, setRecentCount] = useState<number | null>(null);

  const propose = async () => {
    if (!transcript.trim()) return;
    setProposing(true);
    setError(null);
    setRecentCount(null);
    try {
      const res = await fetch("/api/proposals/generate", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ source: source.trim() || "Untitled meeting", transcript }),
        credentials: "include",
      });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setRecentCount(Array.isArray(data.proposals) ? data.proposals.length : 0);
      setTranscript("");
      qc.invalidateQueries({ queryKey: ["proposals"] });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Agent failed");
    } finally {
      setProposing(false);
    }
  };

  const decide = async (id: string, action: "approve" | "reject", overridePatch?: Record<string, unknown>) => {
    const res = await fetch(`/api/proposals/${id}/${action}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(overridePatch ? { patch: overridePatch } : {}),
      credentials: "include",
    });
    if (!res.ok) {
      setError(await res.text().catch(() => `${action} failed`));
      return;
    }
    qc.invalidateQueries({ queryKey: ["proposals"] });
    qc.invalidateQueries({ queryKey: ["deliverables"] });
  };

  const { user } = useAuth();
  const me = resolvePerson(user?.email)?.firstName.toLowerCase() ?? "";
  const [scope, setScope] = useState<"all" | "mine">("all");

  // A proposal is "mine" if the affected task's owner matches me, OR the
  // proposed owner / new-task owner is me.
  const isMine = (p: Proposal): boolean => {
    if (!me) return false;
    const patch = (() => { try { return JSON.parse(p.patchJson || "{}"); } catch { return {}; } })() as Record<string, unknown>;
    const newOwner = String(patch.owner ?? "").toLowerCase();
    if (newOwner && newOwner === me) return true;
    const task = tasks.find((t) => String(t.id) === p.taskId);
    if (task) {
      const taskOwners = String(task.owner ?? "").toLowerCase().split(/[\/,]/).map((s) => s.trim());
      if (taskOwners.includes(me)) return true;
    }
    return false;
  };

  const filteredProposals = useMemo(() => {
    if (scope === "all") return proposals;
    return proposals.filter(isMine);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proposals, scope, me, tasks]);

  const pending = useMemo(() => filteredProposals.filter((p) => p.status === "pending"), [filteredProposals]);
  const rejected = useMemo(() => filteredProposals.filter((p) => p.status === "rejected"), [filteredProposals]);
  const approved = useMemo(() => filteredProposals.filter((p) => p.status === "approved"), [filteredProposals]);
  const myCount = useMemo(() => proposals.filter((p) => isMine(p)).length, [proposals, me, tasks]); // eslint-disable-line react-hooks/exhaustive-deps

  type ProposalView = "pending" | "rejected" | "approved";
  const [view, setView] = useState<ProposalView>("pending");
  const visible = view === "pending" ? pending : view === "rejected" ? rejected : approved;

  const restoreProposal = async (id: string) => {
    const res = await fetch(`/api/proposals/${id}/restore`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: "{}",
      credentials: "include",
    });
    if (!res.ok) {
      setError(await res.text().catch(() => "Restore failed"));
      return;
    }
    qc.invalidateQueries({ queryKey: ["proposals"] });
  };

  // Group the visible list by source meeting
  const grouped = useMemo(() => {
    const m = new Map<string, Proposal[]>();
    for (const p of visible) {
      const arr = m.get(p.source) ?? [];
      arr.push(p);
      m.set(p.source, arr);
    }
    return Array.from(m.entries());
  }, [visible]);

  return (
    <div className="space-y-8">
      <datalist id="roster-firstnames">
        {ROSTER.map((p) => <option key={p.email} value={p.firstName}>{p.name}</option>)}
      </datalist>
      {/* Auto-sync status */}
      <section className="rounded-xl border border-border bg-card p-5">
        <div className="mb-2 flex items-start justify-between gap-3">
          <div className="flex items-center gap-2">
            <Sparkles size={16} className="text-ai-purple" />
            <h2 className="text-h4 font-semibold text-foreground">Auto-sync from Drive</h2>
          </div>
          <button
            onClick={triggerSync}
            disabled={syncing || !syncStatus?.enabled || syncStatus?.inProgress}
            className="inline-flex items-center gap-1 rounded-md bg-primary px-3 py-1.5 text-caption font-medium text-white hover:bg-primary/90 disabled:opacity-50"
          >
            {syncing || syncStatus?.inProgress ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
            {syncing || syncStatus?.inProgress ? "Syncing…" : "Sync now"}
          </button>
        </div>

        {!syncStatus?.enabled ? (
          <p className="text-caption text-muted-foreground">
            Auto-sync isn't configured yet. Set <code>GOOGLE_DRIVE_FOLDER_ID</code> in Railway after Stephen creates and shares the transcripts folder. In the meantime use the manual paste below.
          </p>
        ) : (
          <div className="space-y-2 text-caption text-muted-foreground">
            <div>
              Felix Agent watches a Drive folder and ingests new meeting transcripts every ~5 min. Approved proposals push live changes to the plan.
            </div>
            <div className="flex flex-wrap gap-x-5 gap-y-1 text-micro">
              <span><strong className="text-foreground">Last synced:</strong> {syncStatus.lastSyncAt ? new Date(syncStatus.lastSyncAt).toLocaleString() : "never"}</span>
              <span><strong className="text-foreground">Docs processed:</strong> {syncStatus.processedDocsCount}</span>
              {syncStatus.lastSyncSummary && (
                <span><strong className="text-foreground">Last run:</strong> {syncStatus.lastSyncSummary.processed.length} new · {syncStatus.lastSyncSummary.skipped.length} skipped · {syncStatus.lastSyncSummary.errors.length} errors</span>
              )}
            </div>
          </div>
        )}

        {error && <div className="mt-3 rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-caption text-destructive">{error}</div>}

        {syncStatus?.enabled && processedDocs.length > 0 && (
          <details className="mt-4 border-t border-border pt-4">
            <summary className="cursor-pointer text-caption font-medium text-foreground">
              {processedDocs.length} synced transcript{processedDocs.length === 1 ? "" : "s"} — click to view & re-run
            </summary>
            <ul className="mt-3 space-y-1.5">
              {processedDocs.map((d) => (
                <li key={d.docId} className="flex items-center justify-between gap-3 rounded-md border border-border bg-muted/20 px-3 py-2 text-caption">
                  <div className="min-w-0 flex-1">
                    <div className="truncate font-medium text-foreground">{d.docName || d.docId}</div>
                    <div className="text-micro text-muted-foreground">
                      {new Date(d.processedAt).toLocaleString()} · {d.proposalsCount} proposal{d.proposalsCount === 1 ? "" : "s"}
                    </div>
                  </div>
                  <button
                    onClick={() => rerunDoc(d.docId, d.docName || d.docId)}
                    disabled={rerunning !== null}
                    className="inline-flex shrink-0 items-center gap-1 rounded-md border border-border bg-card px-2 py-1 text-micro text-muted-foreground hover:border-primary/40 hover:bg-primary/5 hover:text-primary disabled:opacity-50"
                  >
                    {rerunning === d.docId ? <Loader2 size={11} className="animate-spin" /> : <RefreshCw size={11} />}
                    Re-run
                  </button>
                </li>
              ))}
            </ul>
          </details>
        )}
      </section>

      {/* Manual paste fallback */}
      <section className="rounded-xl border border-border bg-card">
        <button
          onClick={() => setShowManual((v) => !v)}
          className="flex w-full items-center justify-between gap-2 px-5 py-3 text-left text-caption font-medium text-muted-foreground hover:bg-muted/30"
        >
          <span className="flex items-center gap-2"><FileText size={14} /> Paste a transcript manually (fallback)</span>
          <span>{showManual ? "Hide" : "Show"}</span>
        </button>
        {showManual && (
          <div className="border-t border-border px-5 py-4">
            <p className="mb-3 text-caption text-muted-foreground">
              Use this if you want to test the agent without waiting for auto-sync, or process a one-off doc that isn't in the synced folder.
            </p>
            <div className="space-y-3">
              <input
                value={source}
                onChange={(e) => setSource(e.target.value)}
                placeholder="Meeting label (e.g. Tuesday Planning Call · Apr 28)"
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-caption text-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/20"
              />
              <textarea
                value={transcript}
                onChange={(e) => setTranscript(e.target.value)}
                rows={8}
                placeholder="Paste the meeting transcript here…"
                className="w-full resize-y rounded-md border border-border bg-background px-3 py-2 text-caption text-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/20"
              />
              <div className="flex items-center justify-between gap-3">
                <div className="text-micro text-muted-foreground">
                  {transcript.length.toLocaleString()} chars
                </div>
                <div className="flex items-center gap-3">
                  {recentCount !== null && (
                    <span className="text-caption text-muted-foreground">
                      {recentCount === 0 ? "Agent found no plan-relevant updates." : `${recentCount} proposal${recentCount === 1 ? "" : "s"} added below.`}
                    </span>
                  )}
                  <button
                    onClick={propose}
                    disabled={proposing || !transcript.trim()}
                    className="inline-flex items-center gap-2 rounded-md bg-primary px-3 py-1.5 text-caption font-medium text-white hover:bg-primary/90 disabled:opacity-50"
                  >
                    {proposing ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
                    {proposing ? "Asking the agent…" : "Propose"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </section>

      {/* Status tabs + list */}
      <section>
        <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex gap-1 rounded-md border border-border bg-muted/30 p-1">
              <TabBtn active={view === "pending"} onClick={() => setView("pending")} count={pending.length} label="Pending" tone="primary" />
              <TabBtn active={view === "rejected"} onClick={() => setView("rejected")} count={rejected.length} label="Rejected" tone="rose" />
              <TabBtn active={view === "approved"} onClick={() => setView("approved")} count={approved.length} label="Approved" tone="emerald" />
            </div>
            {me && (
              <div className="flex gap-1 rounded-md border border-border bg-muted/30 p-1">
                <button
                  onClick={() => setScope("all")}
                  className={cn(
                    "rounded-md px-3 py-1.5 text-caption font-medium transition-colors",
                    scope === "all" ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"
                  )}
                >All</button>
                <button
                  onClick={() => setScope("mine")}
                  className={cn(
                    "inline-flex items-center gap-2 rounded-md px-3 py-1.5 text-caption font-medium transition-colors",
                    scope === "mine" ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"
                  )}
                >
                  Mine
                  <span className={cn("rounded-full px-1.5 py-0.5 text-micro", scope === "mine" ? "bg-primary/15 text-primary" : "bg-muted")}>{myCount}</span>
                </button>
              </div>
            )}
          </div>
          {view === "pending" && pending.length > 0 && (
            <button
              onClick={async () => {
                if (!confirm(`Reject all ${pending.length} pending proposals? You can restore them from the Rejected tab.`)) return;
                const res = await fetch("/api/proposals/clear-pending", { method: "POST", credentials: "include" });
                if (res.ok) qc.invalidateQueries({ queryKey: ["proposals"] });
              }}
              className="rounded-md border border-border bg-card px-3 py-1.5 text-caption text-muted-foreground hover:border-destructive/40 hover:bg-destructive/5 hover:text-destructive"
            >
              Clear all pending
            </button>
          )}
          {view === "rejected" && rejected.length > 0 && (
            <button
              onClick={async () => {
                if (!confirm(`Permanently remove ${rejected.length} rejected proposals from this view? This is irreversible.`)) return;
                const res = await fetch("/api/proposals/clear-rejected", { method: "POST", credentials: "include" });
                if (res.ok) qc.invalidateQueries({ queryKey: ["proposals"] });
              }}
              className="rounded-md border border-border bg-card px-3 py-1.5 text-caption text-muted-foreground hover:border-destructive/40 hover:bg-destructive/5 hover:text-destructive"
            >
              Clear all rejected
            </button>
          )}
        </div>

        {isLoading && <div className="text-caption text-muted-foreground">Loading…</div>}
        {!isLoading && visible.length === 0 && (
          <div className="rounded-lg border border-dashed border-border p-6 text-center text-caption text-muted-foreground">
            {view === "pending" && "No proposals waiting. Paste a transcript above (or sync from Drive) to generate some."}
            {view === "rejected" && "Nothing rejected yet."}
            {view === "approved" && "No approved proposals yet — approving one applies it to the plan and lands here."}
          </div>
        )}
        <div className="space-y-6">
          {grouped.map(([src, items]) => (
            <div key={src}>
              <div className="mb-2 flex items-center gap-2 text-caption font-medium text-muted-foreground">
                <span>📞 {src}</span>
                <span>·</span>
                <span>{items.length} proposal{items.length === 1 ? "" : "s"}</span>
              </div>
              <ul className="space-y-2">
                {items.map((p) => (
                  <ProposalCard
                    key={p.id}
                    p={p}
                    tasks={tasks}
                    onApprove={(override) => decide(p.id, "approve", override)}
                    onReject={() => decide(p.id, "reject")}
                    onRestore={() => restoreProposal(p.id)}
                  />
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

function TabBtn({ active, onClick, count, label, tone }: {
  active: boolean;
  onClick: () => void;
  count: number;
  label: string;
  tone: "primary" | "rose" | "emerald";
}) {
  const toneClasses = {
    primary: active ? "bg-primary/10 text-primary" : "text-muted-foreground hover:text-foreground",
    rose: active ? "bg-rose-100 text-rose-700" : "text-muted-foreground hover:text-foreground",
    emerald: active ? "bg-emerald-100 text-emerald-700" : "text-muted-foreground hover:text-foreground",
  }[tone];
  return (
    <button
      onClick={onClick}
      className={cn(
        "inline-flex items-center gap-2 rounded-md px-3 py-1.5 text-caption font-medium transition-colors",
        toneClasses
      )}
    >
      {label}
      <span className={cn("rounded-full px-1.5 py-0.5 text-micro", active ? "bg-white/40" : "bg-muted")}>{count}</span>
    </button>
  );
}
